import { CheckoutProps, OnSubmitSuccessArgs } from '@wix/checkout-components';
import {
  PaymentCycle,
  PaymentCycle_BIMap,
  stringifyDefinedQueryParams,
} from '../../../../utils';
import { CheckoutApi, CheckoutUrls } from '../../../../Api/checkout';
import { ApiManager } from '../../../../Api';
import {
  BiReporter,
  CheckoutBiLogParams,
  FedopsReporter,
  Product,
} from '../../../../types';
import { useCheckoutStore } from '../../../../providers';

type QueryParamsMap = Record<string, any>;

interface RedirectBaseParams {
  siteGuid: string;
  invoiceId: string;
}

interface PremiumSuccessPageParams extends RedirectBaseParams {
  baseUrl: string;
  eventAggregator: string;
  secondUpgradeSuccess?: boolean;
  domainName?: string | null;
}

interface RedirectToSuccessFromUrlParams {
  invoiceId: string;
  planName: string;
  currentCycle: PaymentCycle;
  successUrl: string;
}

interface RedirectToPlanSuccessPageParams {
  baseUrl: string;
  siteGuid: string;
  invoiceId: string;
  siteName: string;
  isUpgrade: boolean;
  eventAggregator: string;
  replaceDomainVoucherWithPlanDiscount: boolean;
  isNewMobilePremiumSuccessPageEnabled: boolean;
  isPremiumSuccessPageUrlEnabled: boolean;
  isReassignSuccessPageEnabled: boolean;
  isSecondUpgradeNewSuccessPageEnabled: boolean;
}

interface RedirectToSuccessPageParams {
  siteName: string;
  successUrl?: string;
  oldSiteGuid?: string;
  redirectFromUrlParams: RedirectToSuccessFromUrlParams;
  redirectToSuccessPageParams: RedirectToPlanSuccessPageParams;
}

export interface UseOnSubmitSuccessOptions {
  baseUrl: string;
  isDayful: boolean;
  siteGuid: string;
  siteName: string;
  isUpgrade: boolean;
  eventAggregator: string;
  currentProduct: Product;
  currentCycle: PaymentCycle;
  replaceDomainVoucherWithPlanDiscount: boolean;
  isPremiumSuccessPageUrlEnabled: boolean;
  isReassignSuccessPageEnabled: boolean;
  isSecondUpgradeNewSuccessPageEnabled: boolean;
  isNewMobilePremiumSuccessPageEnabled: boolean;
  isRelativeSepaSuccessRedirectEnabled: boolean;
  secondUpgradeSuccess?: boolean;
  domainName?: string | null;
  oldSiteGuid?: string;
  successUrl?: string;
  isDowngradePlanOfDeclineChargeService?: boolean;
  removeUnloadListener: () => void;
  onSubmitSuccessStarted: FedopsReporter;
  sendPurchasePendingBiLog: BiReporter<CheckoutBiLogParams>;
  sendPurchaseSuccessBiLog: BiReporter<CheckoutBiLogParams>;
  endSubmitOrderPageInteraction: FedopsReporter;
}

const PREMIUM_SUCCESS_PAGES_BASE_URL = 'premium-success-pages';

enum Referrers {
  Dayful = 'PurchaseDayful',
  Premium = 'PremiumPurchasePlan',
}

enum InvoiceStatuses {
  PaymentInProgress = 'PAYMENT_IN_PROGRESS',
  Draft = 'DRAFT',
}

const SuccessPageUrls = {
  Dayful: `${PREMIUM_SUCCESS_PAGES_BASE_URL}/dayful/`,
  Reassign: `${PREMIUM_SUCCESS_PAGES_BASE_URL}/reassign/`,
};

function generateUrlWithQueryParams(url: string, queryParams: QueryParamsMap) {
  return encodeURI(
    Object.entries(queryParams).reduce(
      (acc, [paramName, paramValue], index) =>
        acc + `${index ? '&' : '?'}${paramName}=${paramValue}`,
      url,
    ),
  );
}

function getPremiumSuccessPagesUrl({
  siteGuid,
  invoiceId,
  baseUrl,
  eventAggregator,
  secondUpgradeSuccess,
  domainName,
}: PremiumSuccessPageParams) {
  const params = {
    referrer: Referrers.Premium.toString(),
    siteId: siteGuid,
    externalSessionId: eventAggregator,
    secondUpgrade: secondUpgradeSuccess ? secondUpgradeSuccess?.toString() : '',
    domainName: domainName ?? '',
  };
  const queryParamsStr = stringifyDefinedQueryParams(params);
  return `${baseUrl}${PREMIUM_SUCCESS_PAGES_BASE_URL}/${invoiceId}?${queryParamsStr}`;
}

async function fetchSiteDomainName(siteGuid: string) {
  try {
    const assignedDomainsUrl = `${CheckoutUrls.GET_ASSIGNED_DOMAINS}?filter.msid=${siteGuid}`;
    const res = await ApiManager.get<any>(assignedDomainsUrl);
    return res?.data?.sites[0]?.primary?.domainName;
  } catch {
    return undefined;
  }
}

async function getSiteDomainName(
  siteGuid: string,
  isUpgrade: boolean,
  isSecondUpgradeNewSuccessPageEnabled: boolean,
) {
  if (isSecondUpgradeNewSuccessPageEnabled && isUpgrade) {
    return fetchSiteDomainName(siteGuid);
  }
  return undefined;
}

function navigateToSuccessPage(url: string, queryParams: QueryParamsMap) {
  const urlWithParams = generateUrlWithQueryParams(url, queryParams);
  window.location.assign(urlWithParams);
}

function redirectToDayfulSuccessPage(
  baseUrl: string,
  siteGuid: string,
  invoiceId: string,
) {
  const queryParams = {
    siteId: siteGuid,
    referrer: Referrers.Dayful,
  };
  navigateToSuccessPage(
    `${baseUrl}${SuccessPageUrls.Dayful}${invoiceId}`,
    queryParams,
  );
}

function redirectToSiteLevelSubscriptionPage(
  baseUrl: string,
  siteGuid: string,
): void {
  window.location.assign(
    `${baseUrl}dashboard/${siteGuid}/settings/subscriptions?referralInfo=AdvancedPlans_downgradePlan`,
  );
}

function redirectToSepaPendingPage(
  siteGuid: string,
  invoiceId: string,
  isRelativeSepaSuccessRedirectEnabled: boolean,
): void {
  const queryParams = new URLSearchParams(`referrer=${Referrers.Premium}`);
  if (siteGuid) {
    queryParams.append('siteId', siteGuid);
  }

  const sepaPendingPage = isRelativeSepaSuccessRedirectEnabled
    ? `/${PREMIUM_SUCCESS_PAGES_BASE_URL}/${invoiceId}?${queryParams.toString()}`
    : `https://premium.wix.com/wix/api/SEPAPaymentPending?v=1&invoiceId=${invoiceId}&referralAdditionalInfo=premium-purchase-plan`;

  window.location.assign(sepaPendingPage);
}

function redirectToBoletoSuccessPage(
  successPageParams: PremiumSuccessPageParams,
): void {
  const boletoSuccessPageUrl = getPremiumSuccessPagesUrl(successPageParams);
  window.location.assign(boletoSuccessPageUrl);
}

function redirectToSubscriptionsPage(siteName: string) {
  window.location.assign(
    generateUrlWithQueryParams('/wix/api/PaymentConsole', {
      referralAdditionalInfo: 'package-picker',
      showAssignToast: true,
      targetSite: siteName,
    }),
  );
}

function redirectToSuccessFromUrl({
  invoiceId,
  planName,
  currentCycle,
  successUrl,
}: RedirectToSuccessFromUrlParams) {
  const params = {
    invoiceId,
    planName,
    cycle: currentCycle,
    paymentType: 'IMMIDIATE',
  };
  const urlPath = generateUrlWithQueryParams(successUrl, params);
  window.location.assign(urlPath);
}

function redirectToSecondUpgradeSuccessPage(
  redirectParams: PremiumSuccessPageParams,
) {
  const url = getPremiumSuccessPagesUrl({
    ...redirectParams,
    secondUpgradeSuccess: true,
  });
  window.location.assign(url);
}

async function redirectToPlanSuccessPage({
  baseUrl,
  siteGuid,
  invoiceId,
  siteName,
  isUpgrade,
  eventAggregator,
  replaceDomainVoucherWithPlanDiscount,
  isReassignSuccessPageEnabled,
  isPremiumSuccessPageUrlEnabled,
  isSecondUpgradeNewSuccessPageEnabled,
}: RedirectToPlanSuccessPageParams) {
  const currentUrl = new URLSearchParams(window.location.search);
  const referralAdditionalInfo =
    currentUrl.get('referralAdditionalInfo') ?? Referrers.Premium;
  const domainName = currentUrl.get('domainName');
  const isConnectIntent =
    referralAdditionalInfo === 'add-domain-connect-intent';

  const baseQueryParams = {
    invoiceId,
    siteName,
    isAnonymousDomain: replaceDomainVoucherWithPlanDiscount,
    referrer: referralAdditionalInfo,
  };

  const reassignRequestParams = {
    ...baseQueryParams,
    metaSiteId: siteGuid,
    isUpgrade,
    ...(isConnectIntent && { reassignStatus: domainName }),
  };
  // TODO: Move getIsRepurchase API conditioning into premium-success-pages when PP redirects traffic to it by default
  const reassignStatus = await CheckoutApi.fetchIsRepurchase(
    reassignRequestParams,
  );
  const isReassignIntent =
    !isUpgrade &&
    isReassignSuccessPageEnabled &&
    (isConnectIntent || reassignStatus);

  const siteDomainName = await getSiteDomainName(
    siteGuid,
    isUpgrade,
    isSecondUpgradeNewSuccessPageEnabled,
  );
  if (siteDomainName) {
    redirectToSecondUpgradeSuccessPage({
      baseUrl,
      invoiceId,
      siteGuid,
      eventAggregator,
      domainName: siteDomainName,
    });
  } else if (isPremiumSuccessPageUrlEnabled) {
    const premiumUrlSuccessPageParams = {
      siteId: siteGuid,
      ...baseQueryParams,
    };
    navigateToSuccessPage(
      `${baseUrl}${PREMIUM_SUCCESS_PAGES_BASE_URL}`,
      premiumUrlSuccessPageParams,
    );
  } else if (isReassignIntent) {
    const reassignQueryParams = {
      ...reassignRequestParams,
      reassignStatus,
    };
    navigateToSuccessPage(
      `${baseUrl}${SuccessPageUrls.Reassign}${invoiceId}`,
      reassignQueryParams,
    );
  } else {
    navigateToSuccessPage(`${baseUrl}store/plansSuccessPage`, {
      invoiceId,
      referralAdditionalInfo,
      newPurchase: !isUpgrade,
    });
  }
}

async function redirectToSuccessPage({
  oldSiteGuid,
  siteName,
  successUrl,
  redirectFromUrlParams,
  redirectToSuccessPageParams,
}: RedirectToSuccessPageParams) {
  if (oldSiteGuid) {
    redirectToSubscriptionsPage(siteName);
  } else if (successUrl) {
    redirectToSuccessFromUrl(redirectFromUrlParams);
  } else {
    await redirectToPlanSuccessPage(redirectToSuccessPageParams);
  }
}

export function useOnSubmitSuccess(
  options?: UseOnSubmitSuccessOptions,
): CheckoutProps['onSubmitSuccess'] | undefined {
  const { state } = useCheckoutStore();
  if (options) {
    const {
      siteGuid,
      baseUrl,
      siteName,
      eventAggregator,
      secondUpgradeSuccess,
      domainName,
      successUrl,
      oldSiteGuid,
      currentProduct,
      currentCycle,
      isDayful,
      isUpgrade,
      isPremiumSuccessPageUrlEnabled,
      isSecondUpgradeNewSuccessPageEnabled,
      isReassignSuccessPageEnabled,
      replaceDomainVoucherWithPlanDiscount,
      isDowngradePlanOfDeclineChargeService,
      isRelativeSepaSuccessRedirectEnabled,
      isNewMobilePremiumSuccessPageEnabled,
      onSubmitSuccessStarted,
      endSubmitOrderPageInteraction,
      removeUnloadListener,
      sendPurchasePendingBiLog,
      sendPurchaseSuccessBiLog,
    } = options;

    return async ({ invoiceId, invoiceStatus }: OnSubmitSuccessArgs) => {
      removeUnloadListener && removeUnloadListener();
      endSubmitOrderPageInteraction();
      onSubmitSuccessStarted();
      const biParams = {
        productGuid: currentProduct.guid,
        productId: currentProduct.id,
        eventAggregator: state.orderPageEventAggregator,
        cycle: PaymentCycle_BIMap[currentCycle] ?? 0,
      };
      if (isDayful) {
        redirectToDayfulSuccessPage(baseUrl, siteGuid, invoiceId);
      } else if (isDowngradePlanOfDeclineChargeService) {
        redirectToSiteLevelSubscriptionPage(baseUrl, siteGuid);
      } else if (invoiceStatus === InvoiceStatuses.PaymentInProgress) {
        sendPurchasePendingBiLog(biParams);
        redirectToSepaPendingPage(
          siteGuid,
          invoiceId,
          isRelativeSepaSuccessRedirectEnabled,
        );
      } else if (invoiceStatus === InvoiceStatuses.Draft) {
        redirectToBoletoSuccessPage({
          baseUrl,
          siteGuid,
          invoiceId,
          eventAggregator,
          secondUpgradeSuccess,
          domainName,
        });
      } else if (invoiceId) {
        sendPurchaseSuccessBiLog(biParams);
        await redirectToSuccessPage({
          siteName,
          successUrl,
          oldSiteGuid,
          redirectFromUrlParams: {
            invoiceId,
            planName: currentProduct.productCode,
            currentCycle,
            successUrl: successUrl ?? '',
          },
          redirectToSuccessPageParams: {
            invoiceId,
            baseUrl,
            siteGuid,
            siteName,
            isUpgrade,
            eventAggregator,
            replaceDomainVoucherWithPlanDiscount,
            isSecondUpgradeNewSuccessPageEnabled,
            isNewMobilePremiumSuccessPageEnabled,
            isReassignSuccessPageEnabled,
            isPremiumSuccessPageUrlEnabled,
          },
        });
      }
    };
  }

  return undefined;
}
